import { MouseEventHandler, useEffect, useMemo, useState } from 'react';

import { Divider } from '@material-ui/core';
import { Button, Grid, Step, stepLabelClasses, Stepper, Typography } from '@mui/material';
import { X } from 'phosphor-react';

import { PointerCatcher } from '~components/hooks/utils/usePointerCatcher';
import { useTransactionProvider } from '~components/providers/TransactionProvider';
import { TxStatus } from '~components/providers/types';
import Link from '~components/shared/link';
import { black, secondaryColors, statusOrange, statusRed } from '~components/theme/styles/colors';
import { columnGap, defaultBackground } from '~components/theme/styles/mixins';

import { GradientStepLabel } from '../../stepper/gradientStepLabel';

import { TransactionNotificationIcon } from './transactionNotificationIcon';

export const TransactionNotifications: React.FC<{ viewAllTransactions: MouseEventHandler }> = ({
  viewAllTransactions,
}) => {
  const [openDetails, setOpenDetails] = useState(false);
  const { transactions, currentTransactionKey, prevTransactionKey, showTransaction } =
    useTransactionProvider();

  const currentTransaction = transactions[currentTransactionKey];
  const prevTransaction = transactions[prevTransactionKey];

  const { transactionSteps, activeStep } = useMemo(() => {
    const transactionSteps: {
      stepTitle: string;
      status?: TxStatus;
      isTransacting?: boolean;
      url?: string;
    }[] = [];
    let activeStep = 0;

    // step 1 cases

    if (prevTransaction) {
      const { status, isTransacting, url, key } = prevTransaction;

      transactionSteps.push({
        stepTitle: key.replaceAll('-', ' '),
        status,
        isTransacting,
        url,
      });
      // nextStepName determines whether the transaction is part of the first step or second step
    } else if (!currentTransaction?.nextStepName) {
      transactionSteps.push({
        stepTitle: 'Approved',
        status: TxStatus.idle,
        isTransacting: false,
      });
    } else if (currentTransaction) {
      const { status, isTransacting, url, key } = currentTransaction;
      transactionSteps.push({
        stepTitle: key.replaceAll('-', ' '),
        status,
        isTransacting,
        url,
      });
    }

    // step 2 cases
    if (currentTransaction?.nextStepName) {
      transactionSteps.push({
        stepTitle: currentTransaction.nextStepName.replaceAll('-', ' '),
      });
    } else if (currentTransaction) {
      const { status, isTransacting, url, key } = currentTransaction;
      transactionSteps.push({
        stepTitle: key.replaceAll('-', ' '),
        status,
        isTransacting,
        url,
      });
      activeStep = 1;
    }

    if (currentTransaction?.status === TxStatus.success && !currentTransaction?.nextStepName) {
      activeStep = 2;
    }

    return { transactionSteps, activeStep };
  }, [JSON.stringify(currentTransaction), JSON.stringify(prevTransaction)]);

  useEffect(() => {
    const currentStatus = currentTransaction?.status;
    if (currentStatus === TxStatus.success || currentStatus === TxStatus.error) {
      setOpenDetails(true);
    }
  }, [currentTransaction?.status]);

  let transactionIcon = null;

  if (openDetails) {
    transactionIcon = <X size={16} />;
  } else {
    transactionIcon = (
      <TransactionNotificationIcon
        status={currentTransaction?.status}
        isTransacting={currentTransaction?.isTransacting}
      />
    );
  }

  return showTransaction ? (
    <Grid sx={{ position: 'relative', zIndex: 1900, pointerEvents: 'all' }}>
      <Button
        sx={{
          display: 'flex',
          margin: 'auto',
          borderRadius: '50%',
          background: black['45%'],
          width: '32px',
          height: '32px',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 0,
          minWidth: 0,
          '&:hover': {
            background: black['45%'],
          },
        }}
        onClick={() => {
          setOpenDetails(prev => !prev);
        }}
      >
        {transactionIcon}
      </Button>
      {openDetails && (
        <PointerCatcher
          onOutside={() => {
            setOpenDetails(false);
          }}
        >
          <Grid
            sx={{
              position: 'absolute',
              top: 0,
              right: '64px',
              padding: '32px',
              width: '341px',
              ...columnGap(32),
              ...defaultBackground,
            }}
          >
            <Grid>
              <Stepper activeStep={activeStep + 1} orientation="vertical">
                {transactionSteps.map(({ stepTitle, status, isTransacting, url }, index) => (
                  <Step key={index}>
                    <GradientStepLabel
                      sx={{
                        [`.${stepLabelClasses.iconContainer} div`]: {
                          marginRight: '16px',
                        },
                      }}
                    >
                      <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="label3"
                          sx={{
                            color:
                              activeStep === index
                                ? status === TxStatus.error
                                  ? statusRed[100]
                                  : secondaryColors[200]
                                : undefined,
                          }}
                        >
                          {stepTitle}
                        </Typography>
                        {status && (
                          <TransactionNotificationIcon status={status} isTransacting={isTransacting} />
                        )}
                      </Grid>
                      {!!url && (
                        <Link href={url} target="_blank">
                          <Typography variant="label3">VIEW TX ON EXPLORER</Typography>
                        </Link>
                      )}
                    </GradientStepLabel>
                  </Step>
                ))}
                <Step key={2}>
                  <GradientStepLabel
                    sx={{
                      [`.${stepLabelClasses.iconContainer} div`]: {
                        marginRight: '16px',
                      },
                    }}
                  >
                    <Typography
                      variant="label3"
                      sx={{ color: activeStep === 2 ? secondaryColors[200] : undefined }}
                    >
                      COMPLETE
                    </Typography>
                  </GradientStepLabel>
                </Step>
              </Stepper>
            </Grid>
            <Grid>
              <Typography variant="body3" sx={{ color: statusOrange[900] }}>
                Note:
              </Typography>
              <Typography variant="body3" sx={{ color: statusOrange[500] }}>
                Please “Confirm” the transaction in your wallet to continue...{' '}
              </Typography>
            </Grid>
            <Divider sx={{ margin: 0 }} />
            <Button
              variant="secondary"
              onClick={e => {
                viewAllTransactions(e);
                setOpenDetails(false);
              }}
            >
              VIEW ALL TRANSACTIONS HISTORY
            </Button>
          </Grid>
        </PointerCatcher>
      )}
    </Grid>
  ) : null;
};
